<template>
  <div class="uk-card-body p-0">
    <!-- Header with Download Button -->
    <div class="d-flex justify-content-between align-items-center px-3 pb-1" style="border-bottom: 1px solid #eee">
      <h5 class="mb-0"><i class="uil-table"/> {{ $t('general.add_with_excell') }}</h5>
      <button class="btn btn-info template-download-btn" type="button" @click="downloadSampleFile">
        <i class="uil-download-alt"/> {{ $t('general.download_excel_template') }}
        <i class="uil-info-circle ml-1"
           style="font-size: 1rem"
           :uk-tooltip="`title: ${$t('general.excel_template_download_info')}; pos: bottom-center`"
        />
      </button>
    </div>

    <!-- File Upload Area - Full Width -->
    <div class="file-upload-container p-3">
        <div :class="{'file-upload-disabled': isLoading, 'file-upload-has-file': selectedFile}"
             class="file-upload-area">
          <input
              id="fileToUpload"
              ref="fileInput"
              :disabled="isLoading"
              accept=".xls, .xlsx"
              class="file-upload-input"
              name="fileToUpload"
              type="file"
              @change="handleFileSelection"
          />
          <div class="file-upload-content">
            <i class="uil-cloud-upload file-upload-icon"/>
            <p class="file-upload-text">
              {{ selectedFile ? selectedFileName : $t('general.excel_drag_drop') }}
            </p>
          </div>
        </div>
      </div>

    <!-- Data Display Area -->
    <div class="d-flex flex-column justify-content-between">
      <!-- Sheet Tabs Navigation -->
      <div v-if="sheets.length > 0" class="sheet-tabs-container mb-1">
        <ul class="sheet-tabs">
          <li v-for="(sheet, index) in sheets"
              :key="index"
              :class="{ 'uk-active': activeSheetIndex === index }"
              class="sheet-tab-item"
              @click="switchSheet(index)">
            <a href="javascript:void(0)" @click.prevent="switchSheet(index)">{{ sheet.name }}</a>
          </li>
        </ul>
      </div>

      <!-- Success Message -->
      <div v-if="successMessage" class="uk-alert-success mb-3 mx-3" uk-alert>
        <a class="uk-alert-close" uk-close @click="successMessage = null"></a>
        <p style="color: green">
          <i class="uil-check-circle"></i> {{ successMessage }}
        </p>
      </div>

      <!-- Error Messages -->
      <div v-if="errorMessages.length > 0" class="uk-alert-danger mb-3 mx-3" uk-alert>
        <a class="uk-alert-close" uk-close @click="errorMessages = []"></a>
        <div style="color: red">
          <p><i class="uil-exclamation-triangle"></i> <strong> {{ $t('general.error') }} </strong></p>
          <ul style="margin-top: 10px; margin-bottom: 0; padding-left: 20px;">
            <li v-for="(error, index) in errorMessages" :key="index" style="margin-bottom: 5px;">
              {{ error }}
            </li>
          </ul>
        </div>
      </div>

      <default-loading v-if="isLoading"/>
      <div v-else-if="displayedRows.length > 0" class="uk-position-relative">
        <div class="table-responsive table-scroll-container">
          <table class="table align-items-center">
            <thead>
            <tr>
              <th scope="col"></th>

              <th v-for="(header, index) in currentTableHeaders" :key="index" scope="col">
                <span class="header-text">{{ header }}</span>
              </th>
            </tr>
            </thead>
            <tbody class="list">
            <tr v-for="(item, index) in displayedRows" :key="index">
              <td>{{ index + 1 }}</td>
              <td v-for="(header, colIndex) in currentTableHeaders" :key="colIndex">
                {{ item[colIndex] }}
              </td>
            </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div v-if="displayedRows.length > 0" class="text-right mb-3">
        <button class="add-btn uk-button" type="button" @click="uploadExcelFile">
          <i class="uil-plus-circle"/> {{
            isSaving ? this.$t('general.saving') : this.$t('general.save')
          }}
        </button>
      </div>

    </div>
  </div>
</template>

<script>
import DefaultLoading from "@/view/components/loading/DefaultLoading.vue";
import axios from "axios";
import moment from "moment/moment";
import readXlsxFile from "read-excel-file";
import generalMixin from "@/core/mixin/general.mixin";
import { showSuccess } from "@/utils/alertUtils";

export default {
  name: "MultiPageExcelReader",
  mixins: [generalMixin],
  components: { DefaultLoading },

  props: {
    // API endpoint for downloading sample Excel
    sampleExcelEndpoint: {
      type: String,
      required: true
    },
    // Default file name for downloaded sample
    sampleFileName: {
      type: String,
      default: "excel_sample"
    },
    excelHasHeader: {
      type: Boolean,
      default: true
    }
  },

  data() {
    return {
      isLoading: false,
      successMessage: null,
      errorMessages: [],
      selectedFile: null,
      selectedFileName: null,
      fileInfo: {
        name: "",
        count: 0,
        file: null,
      },
      sheets: [],
      activeSheetIndex: 0,
      tableHeaders: [],
      processedRows: [],
      isSaving: false
    };
  },

  computed: {
    displayedRows() {
      const activeSheet = this.sheets[this.activeSheetIndex];
      return activeSheet?.data || this.processedRows || [];
    },

    currentTableHeaders() {
      const activeSheet = this.sheets[this.activeSheetIndex];
      return activeSheet?.headers || this.tableHeaders || [];
    },
  },

  methods: {
    switchSheet(sheetIndex) {
      if (sheetIndex >= 0 && sheetIndex < this.sheets.length) {
        this.activeSheetIndex = sheetIndex;
      }
    },

    handleFileSelection(event) {
      const selectedFile = event.target.files[0];
      if (!selectedFile) return;

      this.resetPageData();
      this.selectedFile = selectedFile;
      this.selectedFileName = selectedFile.name;
      this.fileInfo.file = selectedFile;
      this.fileInfo.name = this.selectedFileName;
      this.loadExcelFile();
    },

    resetPageData() {
      this.selectedFile = null;
      this.selectedFileName = null;
      this.sheets = [];
      this.activeSheetIndex = 0;
      this.tableHeaders = [];
      this.processedRows = [];
      this.fileInfo = {
        name: "",
        count: 0,
        file: null,
      };
      this.isSaving = false;
      this.successMessage = null;
      this.errorMessages = [];

      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = "";
      }
    },

    async loadExcelFile() {
      if (!this.selectedFile) return;

      this.isLoading = true;
      this.errorMessages = [];
      this.fileInfo.file = this.selectedFile;

      try {
        await this.readExcelWithXLSX();
      } catch (error) {
        console.warn('xlsx library not available, using read-excel-file:', error);
        if (this.selectedFile) {
          this.readExcelWithReadExcelFile();
        }
      }
    },

    async readExcelWithXLSX() {
      let xlsxLibrary = await this.loadXLSXLibrary();
      if (xlsxLibrary) {
        await this.processExcelFileWithXLSX(xlsxLibrary);
      } else {
        this.isLoading = false;
        throw new Error('XLSX library not available');
      }
    },

    async loadXLSXLibrary() {
      try {
        const xlsxModule = await import('xlsx');
        return xlsxModule.default || xlsxModule;
      } catch (e) {
        if (window.XLSX) {
          return window.XLSX;
        }
        
        return new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = 'https://cdn.sheetjs.com/xlsx-0.20.0/package/dist/xlsx.full.min.js';
          script.onload = () => resolve(window.XLSX);
          script.onerror = () => {
            this.isLoading = false;
            reject(new Error('xlsx library could not be loaded'));
          };
          document.head.appendChild(script);
        });
      }
    },

    processExcelFileWithXLSX(xlsxLibrary) {
      return new Promise((resolve, reject) => {
        const fileReader = new FileReader();

        fileReader.onload = (event) => {
          try {
            const fileData = new Uint8Array(event.target.result);
            const workbook = xlsxLibrary.read(fileData, { type: 'array' });

            if (!workbook.SheetNames || workbook.SheetNames.length === 0) {
              throw new Error('Excel dosyasında sheet bulunamadı.');
            }

            this.sheets = this.extractSheetsFromWorkbook(workbook, xlsxLibrary);
            
            if (this.sheets.length === 0) {
              throw new Error('Excel dosyasında geçerli sheet bulunamadı.');
            }

            this.initializeFirstSheet();
            this.updateFileInfo();
            this.isLoading = false;
            resolve();

          } catch (error) {
            console.error('Error processing Excel file:', error);
            this.errorMessages.push(this.$t('general.excel_process_error'));
            this.isLoading = false;
            reject(error);
          }
        };

        fileReader.onerror = () => {
          this.errorMessages.push(this.$t('general.excel_read_error'));
          this.isLoading = false;
          reject(new Error("Dosya okunamadı."));
        };

        fileReader.readAsArrayBuffer(this.selectedFile);
      });
    },

    extractSheetsFromWorkbook(workbook, xlsxLibrary) {
      return workbook.SheetNames
        .map(sheetName => {
          const worksheet = workbook.Sheets[sheetName];
          if (!worksheet) {
            console.warn(`Sheet "${sheetName}" bulunamadı, atlanıyor.`);
            return null;
          }

          const sheetData = xlsxLibrary.utils.sheet_to_json(worksheet, {
            header: 1,
            defval: ''
          });

          const { headers, rows } = this.parseSheetData(sheetData);

          return {
            name: sheetName,
            headers: headers,
            data: rows,
            originalData: sheetData
          };
        })
        .filter(sheet => sheet !== null);
    },

    parseSheetData(sheetData) {
      const hasHeader = this.excelHasHeader && sheetData.length > 0;
      const headers = hasHeader ? sheetData[0] : [];
      const allRows = hasHeader ? sheetData.slice(1) : sheetData;
      
      const rows = allRows.filter(row => 
        row && row.length > 0 && row.some(cell => cell !== '')
      );

      return { headers, rows };
    },

    initializeFirstSheet() {
      this.activeSheetIndex = 0;
      if (this.sheets.length > 0) {
        this.tableHeaders = this.sheets[0].headers;
        this.processedRows = this.sheets[0].data;
      }
    },

    updateFileInfo() {
      this.fileInfo.name = this.selectedFileName || "";
      this.fileInfo.count = this.sheets.reduce((total, sheet) => total + sheet.data.length, 0);
    },

    readExcelWithReadExcelFile() {
      readXlsxFile(this.selectedFile)
        .then((rows) => {
          if (rows.length < 2 && this.excelHasHeader) {
            this.errorMessages.push(this.$t('general.excel_empty_or_invalid'));
            this.isLoading = false;
            return;
          }

          const { headers, rows: processedRows } = this.parseSheetData(rows);

          this.sheets = [{
            name: this.$t('general.sheet_1'),
            headers: headers,
            data: processedRows,
            originalData: rows
          }];

          this.initializeFirstSheet();
          this.fileInfo.name = this.selectedFileName || "";
          this.fileInfo.count = processedRows.length;
          this.isLoading = false;

        })
        .catch(error => {
          this.errorMessages.push(this.$t('general.excel_read_error'));
          this.isLoading = false;
        });
    },

    uploadExcelFile() {
      if (!this.selectedFile) {
        this.errorMessages.push(this.$t('general.excel_file_not_found'));
        return;
      }

      this.isSaving = true;
      this.isLoading = true;
      this.errorMessages = [];

      const formData = new FormData();
      formData.append('file', this.selectedFile);

      axios.post('api/excel-external-education-upload', formData, {
        headers: {
          ...this.apiHeaders,
          'Content-Type': 'multipart/form-data'
        }
      })
        .then((response) => {
          this.handleUploadSuccess(response);
        })
        .catch((error) => {
          this.handleUploadError(error);
        });
    },

    handleUploadSuccess(response) {
      this.isLoading = false;
      this.isSaving = true;
      this.errorMessages = [];
      
      const message = response.data?.message || response.data?.data?.message || this.$t('general.excel_saved_successfully');
      
      this.resetPageData();
      this.successMessage = message;
      showSuccess(this.$t('general.successfully_uploaded'),5000)

      setTimeout(() => {
        this.successMessage = null;
      }, 5000);
    },

    handleUploadError(error) {
      this.isLoading = false;
      this.isSaving = false;
      this.successMessage = null;
      this.errorMessages = [];

      const errorData = error.response?.data || {};

      if (errorData.errors) {
        this.errorMessages = this.formatValidationErrors(errorData.errors, errorData.message);
      } else {
        this.errorMessages = [errorData.message || errorData.error || error.message || 'Excel dosyası kaydedilirken hata oluştu.'];
      }
    },

    formatValidationErrors(errors, fallbackMessage) {
      const errorList = [];
      Object.keys(errors).forEach((key) => {
        const messages = errors[key];
        if (Array.isArray(messages)) {
          const formattedKey = this.formatErrorKey(key);
          messages.forEach(msg => {
            errorList.push(`${formattedKey}: ${msg}`);
          });
        }
      });
      return errorList.length > 0 ? errorList : [fallbackMessage || 'Excel dosyasında doğrulama hataları bulundu.'];
    },

    formatErrorKey(key) {
      const parts = key.split('.');
      if (parts.length >= 3) {
        const sheetName = parts[0];
        const rowIndex = parseInt(parts[1]) + 1;
        const fieldName = parts.slice(2).join('.');
        return `${sheetName}.${fieldName} (Satır ${rowIndex})`;
      }
      return key;
    },

    downloadSampleFile(event) {
      event.preventDefault();
      this.isLoading = true;

      axios.get(this.sampleExcelEndpoint, {
        responseType: "blob",
      })
        .then((response) => {
          this.downloadBlobAsFile(response.data);
          this.isLoading = false;
        })
        .catch((error) => {
          this.errorMessages.push(this.$t('general.excel_template_download_error'));
          this.isLoading = false;
        });
    },

    downloadBlobAsFile(blobData) {
      const blob = new Blob([blobData]);
      const fileURL = window.URL.createObjectURL(blob);
      const downloadLink = document.createElement("a");
      const timestamp = moment().format("YYYYMMDDHHMMSS");
      const fileName = `${this.sampleFileName}_${timestamp}.xlsx`;

      downloadLink.href = fileURL;
      downloadLink.setAttribute("download", fileName);
      document.body.appendChild(downloadLink);
      downloadLink.click();

      setTimeout(() => {
        document.body.removeChild(downloadLink);
        window.URL.revokeObjectURL(fileURL);
      }, 100);
    }
  }
};
</script>

<style scoped>
.file-upload-container {
  width: 100%;
}

.file-upload-area {
  position: relative;
  border: 2px dashed #ccc;
  border-radius: 6px;
  text-align: center;
  background-color: #f8f9fa;
  transition: all 0.3s ease;
  cursor: pointer;
  overflow: hidden;
  min-height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.file-upload-area:hover {
  border-color: #ae71ff;
  background-color: #f5f0ff;
}

.file-upload-disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.file-upload-has-file {
  border-color: #28a745;
  background-color: #f0fff4;
}

.file-upload-input {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
  z-index: 2;
}

.file-upload-content {
  position: relative;
  z-index: 1;
}

.add-btn {
  padding: 6px 15px;
  font-size: 0.95rem;
  background-color: #28a745;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.add-btn:hover {
  background-color: #218838;
}

.file-upload-text {
  word-wrap: break-word;
}

.sheet-tabs {
  display: flex;
  list-style: none;
  padding: 0;
  margin: 0;
  flex-wrap: wrap;
}

.sheet-tab-item {
  margin-right: 5px;
  margin-bottom: 5px;
}

.sheet-tab-item a {
  display: block;
  padding: 8px 16px;
  text-decoration: none;
  color: #666;
  border: 1px solid #ddd;
  border-bottom: none;
  border-radius: 4px 4px 0 0;
  background-color: #f8f9fa;
  transition: all 0.3s;
  cursor: pointer;
}

.sheet-tab-item:hover a {
  background-color: #e9ecef;
  color: #333;
}

.sheet-tab-item.uk-active a {
  background-color: #fff;
  color: #5864FF;
  border-color: #ddd;
  border-bottom-color: #fff;
  font-weight: 600;
}

.table-scroll-container {
  max-height: 500px;
  overflow-y: auto;
  overflow-x: auto;
  display: block;
}

thead th {
  display: table-cell;
  vertical-align: top;
  padding: 8px;
}

thead th .header-text {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-wrap: break-word;
  white-space: normal;
  max-height: calc(1.5em * 3);
}

</style>